'use client';
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  FC,
  SVGProps,
} from 'react';
import UserProgressStorageService from '@/services/storage/user-progress-storage-service';
import { DefaultConfig } from '@/config';
import Request from '@/services/util/request-service';
import { BRAND_COLOR_PALETTES, type BrandColorPresetName } from '@/config/branding-colors/presets';
import type { BrandColorPalette } from '@/config/branding-colors/types';

export type LogoSrc = { src: string }; // current PNG import shape
export type LogoSvg = FC<SVGProps<SVGSVGElement>>; // an inline SVG component
export type RemoteLogo = string;

// TODO::Set colors differently
export interface Config {
  name: string;
  isSAML?: boolean;
  allowPersonalCourses: boolean;
  logo: LogoSrc | LogoSvg | RemoteLogo;
  colors: BrandColorPalette;
}

export type OrganizationAnalyticsProperties = {
  organization_name: string;
  organization_id?: string;
  organization_domain: string;
};

type BrandingConfigResponse = {
  subdomain?: string;
  displayName?: string;
  colorPresetName?: string;
  logoUrl?: string | null;
};

const DEFAULT_COLOR_PRESET_NAME: BrandColorPresetName = 'default_blue';

const resolveBrandColorPalette = (presetName: string | null | undefined): BrandColorPalette => {
  const normalizedPresetName = String(presetName ?? '').trim() as BrandColorPresetName;
  return (
    BRAND_COLOR_PALETTES[normalizedPresetName] ?? BRAND_COLOR_PALETTES[DEFAULT_COLOR_PRESET_NAME]
  );
};

const isKnownBrandColorPreset = (presetName: string | null | undefined): boolean => {
  const normalizedPresetName = String(presetName ?? '').trim() as BrandColorPresetName;
  return Boolean(BRAND_COLOR_PALETTES[normalizedPresetName]);
};

const getLogoUrlDebugInfo = (
  logoUrl: string | null | undefined,
): {
  logoUrlPresent: boolean;
  logoUrlHost?: string;
  logoUrlPath?: string;
} => {
  if (!logoUrl) {
    return { logoUrlPresent: false };
  }

  try {
    const parsedLogoUrl = new URL(logoUrl);
    return {
      logoUrlPresent: true,
      logoUrlHost: parsedLogoUrl.hostname,
      logoUrlPath: parsedLogoUrl.pathname,
    };
  } catch {
    return {
      logoUrlPresent: true,
      logoUrlHost: 'unparseable',
    };
  }
};

const resolveOrganizationId = (hostname: string, subdomain?: string): string => {
  const normalizedHostname = hostname.toLowerCase();
  if (subdomain && subdomain !== 'default') {
    return subdomain;
  }
  return normalizedHostname.split('.')[0] || 'default';
};

export const resolveConfigFromHostname = (
  hostname: string,
): {
  config: Config;
  organizationId?: string;
} => {
  return {
    config: DefaultConfig,
    organizationId: resolveOrganizationId(hostname),
  };
};

export const getOrganizationAnalyticsProperties = (
  hostname: string,
  config?: Config,
): OrganizationAnalyticsProperties => {
  const { config: resolvedConfig, organizationId } = resolveConfigFromHostname(hostname);
  const activeConfig = config ?? resolvedConfig;

  return {
    organization_name: activeConfig.name,
    organization_domain: hostname,
    ...(organizationId ? { organization_id: organizationId } : {}),
  };
};

interface ConfigContextValue {
  config: Config;
  updateBranding: (updates: {
    colorPresetName?: string;
    displayName?: string;
    logoUrl?: string | null;
  }) => void;
}

const ConfigContext = createContext<ConfigContextValue | null>(null);

export const ConfigProvider = ({ children }: { children: ReactNode }) => {
  const [config, setConfig] = useState<Config | null>(null);

  const applyColorsToDOM = (colors: Config['colors']) => {
    const setCSSVariable = (property: string, value: string) => {
      try {
        if (typeof document !== 'undefined' && document.documentElement?.style) {
          document.documentElement.style.setProperty(property, value);
        }
      } catch (error) {
        console.warn(`Failed to set CSS variable ${property}:`, error);
      }
    };

    setCSSVariable('--color-brand', colors.brand_primary);
    setCSSVariable('--color-brand-highlight', colors.brand_primary_highlight);
    setCSSVariable('--color-brand-mid', colors.brand_primary_mid);
    setCSSVariable('--color-brand-dark', colors.brand_primary_dark);
    setCSSVariable('--color-brand-secondary', colors.brand_secondary);
    setCSSVariable('--color-brand-hover', colors.brand_primary_hover);
    setCSSVariable('--color-brand-accent-start', colors.brand_accent_start);
    setCSSVariable('--color-brand-accent-end', colors.brand_accent_end);
    setCSSVariable('--color-brand-accent-light-start', colors.brand_accent_light_start);
    setCSSVariable('--color-brand-accent-light-end', colors.brand_accent_light_end);
    setCSSVariable('--color-brand-auth-accent-pop', colors.brand_complementary_accent);
    setCSSVariable('--color-brand-auth-gradient-mid', colors.brand_auth_gradient_mid);
    setCSSVariable('--color-brand-auth-gradient-end', colors.brand_auth_gradient_end);
    setCSSVariable('--color-brand-auth-warp-mid', colors.brand_auth_warp_mid);
  };

  useEffect(() => {
    const applyConfig = (resolvedConfig: Config) => {
      setConfig(resolvedConfig);
      applyColorsToDOM(resolvedConfig.colors);
    };

    const loadConfig = async () => {
      try {
        const response = await Request.get('branding/config');
        if (!response) {
          throw new Error('Branding request failed');
        }
        const branding = (await response.json()) as BrandingConfigResponse;
        if (process.env.NODE_ENV === 'development') {
          console.info('[branding] loaded remote config', {
            hostname: window.location.hostname,
            subdomain: branding.subdomain,
            displayName: branding.displayName,
            colorPresetName: branding.colorPresetName,
            presetKnown: isKnownBrandColorPreset(branding.colorPresetName),
            ...getLogoUrlDebugInfo(branding.logoUrl),
          });
        }
        applyConfig({
          ...DefaultConfig,
          name: branding.displayName || DefaultConfig.name,
          logo: branding.logoUrl || DefaultConfig.logo,
          colors: resolveBrandColorPalette(branding.colorPresetName),
        });
      } catch (error) {
        console.warn('Failed to load remote branding config:', error);
        applyConfig({
          ...DefaultConfig,
          colors: BRAND_COLOR_PALETTES[DEFAULT_COLOR_PRESET_NAME],
        });
      }
    };

    void loadConfig();
  }, []);

  // Hydrate user progress from IndexedDB (fallback to network) on initial mount
  useEffect(() => {
    void UserProgressStorageService.validateUserCourseProgressCookie();
  }, []);
  //This applies to new branding instantly without a page refresh
  const updateBranding = (updates: {
    colorPresetName?: string;
    displayName?: string;
    logoUrl?: string | null;
  }) => {
    setConfig((previousConfig) => {
      if (!previousConfig) return previousConfig;

      const updatedPalette = updates.colorPresetName
        ? resolveBrandColorPalette(updates.colorPresetName)
        : previousConfig.colors;
      const updatedLogo = Object.prototype.hasOwnProperty.call(updates, 'logoUrl')
        ? updates.logoUrl || DefaultConfig.logo
        : previousConfig.logo;

      const updatedConfig: Config = {
        ...previousConfig,
        name: updates.displayName ?? previousConfig.name,
        logo: updatedLogo,
        colors: updatedPalette,
      };

      applyColorsToDOM(updatedConfig.colors);
      return updatedConfig;
    });
  };

  if (!config) {
    return <div>Loading...</div>;
  }

  return (
    <ConfigContext.Provider value={{ config, updateBranding }}>{children}</ConfigContext.Provider>
  );
};

export const useConfig = (): ConfigContextValue | null => useContext(ConfigContext);
